import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType, OnInitEffects } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { FareFamiliesResponse } from '@shared/models/fare-families.model';
import { FlightSearchResponse } from '@shared/models/flight-search-response.model';
import { ApiEndpoint } from '@shared/shared/enums/api-endpoints.enum';
import {
  TRAVEL_FLIGHT_SEARCH_LOCAL_STORAGE_KEY,
  TRAVEL_FLIGHT_SEARCH_RESULTS_LOCAL_STORAGE_KEY,
  TRAVEL_SELECTED_FARE_FAMILY_LOCAL_STORAGE_KEY,
  TRAVEL_SELECTED_FLIGHT_LOCAL_STORAGE_KEY,
} from 'app.config';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import * as fromActions from '../actions/flights-search.actions';

@Injectable()
export class FlightsSearchEffects implements OnInitEffects {
  constructor(private actions$: Actions, private http: HttpClient) {}

  public ngrxOnInitEffects(): Action {
    return new fromActions.RestoreFlights();
  }

  public restoreFlights$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.RESTORE_FLIGHTS),
      map(() => {
        const selectedFlightString = localStorage.getItem(TRAVEL_SELECTED_FLIGHT_LOCAL_STORAGE_KEY);
        const flightSearchString = localStorage.getItem(TRAVEL_FLIGHT_SEARCH_LOCAL_STORAGE_KEY);
        const flightSearchResults = JSON.parse(localStorage.getItem(TRAVEL_FLIGHT_SEARCH_RESULTS_LOCAL_STORAGE_KEY));
        const selectedFareFamily = JSON.parse(localStorage.getItem(TRAVEL_SELECTED_FARE_FAMILY_LOCAL_STORAGE_KEY));

        return new fromActions.RestoreFlightsSuccess({
          selectedFlight: selectedFlightString ? JSON.parse(selectedFlightString) : null,
          search: flightSearchString ? JSON.parse(flightSearchString) : null,
          airlines: flightSearchResults?.airlines || [],
          locations: flightSearchResults?.locations || [],
          searchReferenceId: flightSearchResults?.searchReferenceId || '',
          selectedFareFamily: selectedFareFamily ? selectedFareFamily : null,
        });
      })
    )
  );

  public selectFlight$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.SELECT_FLIGHT),
        tap((action: fromActions.SelectFlight) => {
          localStorage.setItem(TRAVEL_SELECTED_FLIGHT_LOCAL_STORAGE_KEY, JSON.stringify(action.payload));
        })
      ),
    { dispatch: false }
  );

  public createFlightsSearch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CREATE_FLIGHTS_SEARCH),
        tap((action: fromActions.CreateFlightsSearch) => {
          localStorage.setItem(TRAVEL_FLIGHT_SEARCH_LOCAL_STORAGE_KEY, JSON.stringify(action.payload));
        })
      ),
    { dispatch: false }
  );

  public clearFlightsSearch$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CLEAR_FLIGHTS_SEARCH),
        tap(() => {
          localStorage.removeItem(TRAVEL_FLIGHT_SEARCH_LOCAL_STORAGE_KEY);
        })
      ),
    { dispatch: false }
  );

  public getFlightSearchResults$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GET_FLIGHT_SEARCH_RESULTS),
      switchMap((action: fromActions.GetFlightSearchResults) => {
        return this.http.post<FlightSearchResponse>(ApiEndpoint.TRAVEL_FLIGHT_SEARCH, action.payload).pipe(
          map((data) => {
            localStorage.setItem(
              TRAVEL_FLIGHT_SEARCH_RESULTS_LOCAL_STORAGE_KEY,
              JSON.stringify({
                airlines: data.airlines,
                locations: data.locations,
                searchReferenceId: data.search_reference_id,
              })
            );
            return new fromActions.GetFlightSearchResultsSuccess(data);
          }),
          catchError((err) => of(new fromActions.GetFlightSearchResultsFailure(err)))
        );
      })
    )
  );

  public getFlightFareFamilies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(fromActions.GET_FLIGHT_FARE_FAMILIES),
      switchMap((action: fromActions.GetFlightFareFamilies) => {
        return this.http.post<FareFamiliesResponse>(ApiEndpoint.TRAVEL_FLIGHT_FARE_FAMILIES, action.payload).pipe(
          map((data) => {
            return new fromActions.GetFlightFareFamiliesSuccess(data);
          })
        );
      })
    )
  );

  public selectFlightFareFamilies$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.SELECT_FLIGHT_FARE_FAMILY),
        tap((action: fromActions.SelectFlightFareFamily) => {
          localStorage.setItem(TRAVEL_SELECTED_FARE_FAMILY_LOCAL_STORAGE_KEY, JSON.stringify(action.payload));
        })
      ),
    { dispatch: false }
  );

  public clearFareFamilies$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(fromActions.CLEAR_FLIGHT_FARE_FAMILIES),
        tap(() => {
          localStorage.removeItem(TRAVEL_SELECTED_FARE_FAMILY_LOCAL_STORAGE_KEY);
        })
      ),
    { dispatch: false }
  );
}
